import * as React from 'react';
import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  useAccordionItemContext,
} from '@reach/accordion';
import type { SerializedStyles } from '@emotion/react';
import { Icon } from '@hotjar/webapp/components/Icon';

import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

const StyledAccordionItem = styled(AccordionItem)`
  border: 1px solid ${tokens.color.ui_border};
  overflow: hidden;
  &[data-state='open'] {
    ${tokens.elevation(1)};
    margin: ${tokens.spacer.x2} 0;
    &:first-of-type {
      margin-top: 0;
    }
  }
  &[data-state='collapsed'] {
    & + [data-reach-accordion-item][data-state='collapsed'] {
      border-top: none;
    }
  }
  &:first-of-type {
    border-top-left-radius: ${tokens.radius.m};
    border-top-right-radius: ${tokens.radius.m};
  }
  &:last-of-type {
    border-bottom-left-radius: ${tokens.radius.m};
    border-bottom-right-radius: ${tokens.radius.m};
  }
`;

const StyledAccordionButton = styled(AccordionButton)`
  font-family: ${tokens.FONT_FAMILY}, sans-serif, Arial;
  border: none;
  background: none;
  padding: ${tokens.spacer.x3} ${tokens.spacer.x4};
  display: flex;
  align-items: center;
  text-align: left;
  width: 100%;
  &:focus {
    outline: none;
  }
  &:hover {
    background-color: ${tokens.color.neutral_100};
  }
`;

const Title = styled.h3`
  ${tokens.text('subtitle1')};
  margin: 0;
  color: ${tokens.color.text_default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Description = styled.span`
  ${tokens.text()};
  margin: 0;
  color: ${tokens.color.text_light};
`;

const Header = styled.div`
  padding: 0 ${tokens.spacer.x4};
  flex-grow: 1;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  padding: ${tokens.spacer.x2} 88px ${tokens.spacer.x4} 88px;
  &:focus {
    outline: none;
  }
`;

interface AccordionBaseProps {
  sectionTitle: string;
  sectionDescription: string;
  iconName: string;
}

type AccordionSectionProps = AccordionBaseProps & {
  dataTestId: string;
  children: React.ReactNode;
  customCss?: SerializedStyles;
};

const AccordionHeader = ({
  sectionTitle,
  sectionDescription,
  iconName,
}: AccordionBaseProps): JSX.Element => {
  const { isExpanded } = useAccordionItemContext();
  return (
    <StyledAccordionButton>
      <Icon color={tokens.color.text_default} icon={iconName} size={24} />
      <Header>
        <Title>{sectionTitle}</Title>
        <Description>{sectionDescription}</Description>
      </Header>
      {isExpanded ? (
        <Icon color={tokens.color.text_default} icon="caret-up" size={14} />
      ) : (
        <Icon color={tokens.color.text_default} icon="caret-down" size={14} />
      )}
    </StyledAccordionButton>
  );
};

/**
 * Please use the Hotsauce version instead
 * @deprecated
 */
export const AccordionSection = ({
  sectionTitle,
  sectionDescription,
  iconName,
  dataTestId,
  children,
  customCss,
}: AccordionSectionProps): JSX.Element => {
  return (
    <StyledAccordionItem data-testid={`accordion-item-${dataTestId}`}>
      <AccordionHeader
        sectionTitle={sectionTitle}
        sectionDescription={sectionDescription}
        iconName={iconName}
      />
      <StyledAccordionPanel css={customCss}>{children}</StyledAccordionPanel>
    </StyledAccordionItem>
  );
};
